import { Alert, Badge, Module, ModuleBody, ModuleHead, ProTable, useCollection, type CollectionColumnDef } from "@vjal/ui";
import { DataTableCollection } from "@vjal/ui"
import type { DataTableColumn } from "@vjal/ui";
import { getExportFamilyBlockerRows } from "../domain/export-blockers";
import { getPermissionDeniedPostureRows, getPermissionExtensionBlockerRows } from "../domain/permission-extension-blockers";
import type { ProductScreenContract, TableRow } from "./product-route-contracts";
import { humanizeStatus } from "../lib/status-format";
import { sessions } from "./product-route-data";
import { screenComponentProvenance } from "./product-screen-provenance";
import { foundationShellOwners, mobileEvidencePaths, screenEvidencePaths } from "./product-screen-registry";

// Routes whose body renders the page's single h1 (PageFrame); the route shell must not add a hero h1.
const bodyPrimaryH1Routes: Record<string, true> = {
  calendar: true,
  capstone: true,
  "capstone-submissions": true,
  certificates: true,
  "clear-series": true,
  "cohort-session-series": true,
  cohorts: true,
  exports: true,
  "my-cohorts": true,
  "org-admin-home": true,
  participants: true,
  programs: true,
  reports: true,
  resources: true,
  "session-reschedule": true,
  sessions: true,
  tools: true
};

export function resolveScreenContract(routeId: string, contract: ProductScreenContract): ProductScreenContract {
  const backendBlocked = contract.surfaceKind === "backend-blocked";
  const compatibilityAlias = contract.surfaceKind === "compatibility-alias";

  return {
    ...contract,
    vjalComponents: contract.vjalComponents ?? registeredVjalComponents(routeId) ?? inferVjalComponents(contract, backendBlocked),
    localCompositions: contract.localCompositions ?? inferLocalCompositions(routeId, contract, compatibilityAlias),
    packageGapRefs: contract.packageGapRefs ?? (backendBlocked ? contract.extensionSlots : []),
    decisionHierarchy: contract.decisionHierarchy ?? inferDecisionHierarchy(contract, backendBlocked, compatibilityAlias),
    requiredViewports: contract.requiredViewports ?? inferRequiredViewports(contract),
    mobileEvidencePaths: contract.mobileEvidencePaths ?? inferMobileEvidencePaths(routeId, contract),
    screenEvidencePaths: contract.screenEvidencePaths ?? (screenEvidencePaths[routeId] ?? []),
    surfaceKind: contract.surfaceKind ?? "product",
    bodyOwnsPrimaryH1:
      contract.bodyOwnsPrimaryH1 ?? ((role) => routeId === "meetings" ? role !== "participant" : bodyPrimaryH1Routes[routeId] === true),
    closureState: contract.closureState ?? inferClosureState(routeId, contract, backendBlocked, compatibilityAlias),
    closureOwner: contract.closureOwner ?? inferClosureOwner(routeId, contract, backendBlocked, compatibilityAlias)
  };
}

function registeredVjalComponents(routeId: string) {
  const refs = screenComponentProvenance[routeId];
  if (!refs) return undefined;
  return [...new Set(refs.flatMap((ref) => ref.components))];
}

export function sessionsListScreen(sessionColumns: () => DataTableColumn<TableRow>[]): ProductScreenContract {
  return {
    title: () => "Sessions",
    primaryJob: "Review visible coaching sessions, invite risk, Zoom readiness, reports, and status handoffs from one scoped list.",
    secondaryJob: "Read-only roles use Session Detail for status visibility while mutation workflow routes stay write-role only.",
    workTitle: "Scoped sessions",
    rows: sessions,
    columns: sessionColumns(),
    states: ["loading", "empty", "error", "denied", "read-only", "status-only"],
    apiFamilies: ["session-service", "artifact-service", "history-service"],
    roleDeltas:
      "Admin and coach see scoped operational sessions; company admin and participant see status-safe sessions without workflow mutation routes.",
    extensionSlots: ["company-admin status view", "participant own schedule", "provider delivery reconciliation"],
    mobilePosture: "Session rows, invite badges, and status actions stack before detail navigation.",
    evidenceRequirement: "Playwright must prove status-safe session rows, participant own schedule, and company-admin read-only session access.",
    vjalComponents: ["Button","CollectionToolbar","IconCalendar","PageFrame","ProTable"],
    localCompositions: ["OperationalSurface", "CapabilityButton", "ActionRow"],
    decisionHierarchy: {
      firstViewport: "Scoped session rows with invite/status badges and role surface label.",
      primaryAction: "Open Session Detail for the selected session.",
      secondaryPanel: "Operational KPIs and action availability for write roles.",
      deferredSurface: "Invite and reschedule mutation routes are reached only by write roles from detail/context.",
      gatedControls: "Read-only roles keep mutation workflow routes unavailable and see status on Session Detail."
    },
    requiredViewports: ["desktop", "mobile"],
    closureState: "foundation-shell",
    closureOwner: "VIL-20260608-0021",
    surfaceKind: "product"
  };
}

export function meetingsAliasScreen(sessionColumns: () => DataTableColumn<TableRow>[]): ProductScreenContract {
  const sessionContract = sessionsListScreen(sessionColumns);

  return {
    ...sessionContract,
    title: () => "Sessions",
    primaryJob: "Old Meetings links open the same Sessions list, so saved bookmarks keep working.",
    secondaryJob: "This compatibility alias must not create a separate product naming model or divergent data rules.",
    workTitle: "Sessions compatibility alias",
    extensionSlots: ["legacy link retirement", "alias redirect proof", "session naming cleanup"],
    evidenceRequirement: "Playwright must prove /meetings renders the Session-compatible list without separate navigation.",
    surfaceKind: "compatibility-alias",
    closureState: "compatibility-alias",
    closureOwner: "VIL-20260608-0018",
    decisionHierarchy: {
      firstViewport: "Session list semantics and role surface label, even when reached from /meetings.",
      primaryAction: "Continue to Session Detail using the standard session route.",
      secondaryPanel: "Compatibility posture is documented in tests and route contracts.",
      deferredSurface: "Future cleanup may redirect /meetings to /sessions after old links are retired.",
      gatedControls: "No Meetings-only mutation or naming behavior is allowed."
    }
  };
}

export function backendBlockedScreen(workTitle: string, featureGate: string): ProductScreenContract {
  const rows = [{ id: featureGate, gate: featureGate, status: "not available yet" }];
  const exportRows = getExportFamilyBlockerRows().map((row) => ({ ...row }));
  const permissionRows = getPermissionExtensionBlockerRows().map((row) => ({ ...row }));
  const permissionDeniedRows = getPermissionDeniedPostureRows().map((row) => ({ ...row }));
  const columns: DataTableColumn<TableRow>[] = [
    { key: "gate", header: "Feature gate" },
    { key: "status", header: "Status", render: (row) => <Badge variant="warning">{humanizeStatus(String(row.status))}</Badge> }
  ];
  const exportColumns: DataTableColumn<TableRow>[] = [
    { key: "role", header: "Role" },
    { key: "entityFamilies", header: "Data families" },
    { key: "availability", header: "Availability", render: (row) => <Badge variant="warning">{String(row.availability)}</Badge> }
  ];
  const permissionColumns: DataTableColumn<TableRow>[] = [
    { key: "extension", header: "Feature" },
    { key: "blocker", header: "Status", render: () => <Badge variant="warning">not available yet</Badge> },
    { key: "auditPrivacyConstraint", header: <span className="whitespace-nowrap">Audit and privacy requirement</span> }
  ];
  const permissionDeniedColumns: DataTableColumn<TableRow>[] = [
    { key: "role", header: "Role" },
    { key: "posture", header: "What that role sees" }
  ];
  const isExports = workTitle === "Exports";
  const isPermissions = workTitle === "Permissions";

  return {
    title: () => workTitle,
    primaryJob: `${workTitle} is not available yet. This page activates once the backing service is ready.`,
    secondaryJob: "No operational records or mutation controls render while the feature is unavailable.",
    workTitle,
    rows: isExports ? exportRows : isPermissions ? permissionRows : rows,
    columns: isExports ? exportColumns : isPermissions ? permissionColumns : columns,
    states: ["blocked-backend", "denied"],
    apiFamilies: [featureGate],
    roleDeltas: "Allowed roles may see the blocker shell; denied roles still receive generic not-found.",
    extensionSlots: [featureGate],
    mobilePosture: "Backend-gated blocker copy remains readable without rendering operational records.",
    evidenceRequirement: "Playwright must prove the route is blocked-unavailable and does not render product data.",
    vjalComponents: ["Alert","Badge","DataTableCollection","Module","ModuleBody","ModuleHead","ProTable"],
    localCompositions: ["AccessState blocker presentation"],
    packageGapRefs: [featureGate],
    decisionHierarchy: {
      firstViewport: "Backend gate and no-data posture.",
      primaryAction: "Do not allow product mutation.",
      secondaryPanel: "Catalog/backend blocker reference.",
      deferredSurface: "Future implementation after API Metadata Catalog support.",
      gatedControls: "All operational controls remain disabled-unavailable or hidden."
    },
    requiredViewports: ["desktop", "mobile"],
    closureState: "backend-blocked",
    closureOwner: featureGate,
    surfaceKind: "backend-blocked",
    surfaceMode: "focused",
    customBody: () => (
      <Module span={12}>
        <ModuleHead title="Feature gate" subtitle={`${workTitle} is not available yet; no records are shown here.`} />
        <ModuleBody className="space-y-4">
          <Alert variant="warning">{workTitle} is not available yet.</Alert>
          {isExports ? (
            <>
              <Alert variant="info">
                Participants have no export option; their own report and resource downloads stay on those pages.
              </Alert>
              <DataTableCollection caption="Export availability by role" columns={exportColumns} data={exportRows} stableChipNames />
            </>
          ) : isPermissions ? (
            <PermissionsBlockedTables
              permissionRows={permissionRows}
              permissionDeniedRows={permissionDeniedRows}
              permissionColumns={permissionColumns}
              permissionDeniedColumns={permissionDeniedColumns}
            />
          ) : (
            <DataTableCollection caption={`${workTitle} availability`} columns={columns} data={rows} stableChipNames />
          )}
        </ModuleBody>
      </Module>
    )
  };
}

function PermissionsBlockedTables({
  permissionRows,
  permissionDeniedRows,
  permissionColumns,
  permissionDeniedColumns,
}: {
  permissionRows: TableRow[];
  permissionDeniedRows: TableRow[];
  permissionColumns: DataTableColumn<TableRow>[];
  permissionDeniedColumns: DataTableColumn<TableRow>[];
}) {
  const featureColumns: CollectionColumnDef<TableRow>[] = [
    {
      id: "extension",
      header: "Feature",
      sortable: true,
      sortAccessor: (row) => String(row.extension ?? ""),
      searchValue: (row) => `${row.extension ?? ""} ${row.blocker ?? ""} ${row.auditPrivacyConstraint ?? ""}`,
      cell: (row) => String(row.extension ?? ""),
    },
    {
      id: "blocker",
      header: "Status",
      cell: () => <Badge variant="warning">not available yet</Badge>,
    },
    {
      id: "auditPrivacyConstraint",
      header: <span className="whitespace-nowrap">Audit and privacy requirement</span>,
      cell: (row) => String(row.auditPrivacyConstraint ?? ""),
    },
  ];
  const featureCollection = useCollection<TableRow>({
    rows: permissionRows,
    columns: featureColumns,
    pageSize: 25,
    getRowKey: (row) => String(row.id ?? row.extension ?? Math.random()),
    views: "coaching-permissions-blockers",
  });
  const deniedColumns: CollectionColumnDef<TableRow>[] = [
    {
      id: "role",
      header: "Role",
      sortable: true,
      sortAccessor: (row) => String(row.role ?? ""),
      searchValue: (row) => `${row.role ?? ""} ${row.posture ?? ""}`,
      cell: (row) => String(row.role ?? ""),
    },
    {
      id: "posture",
      header: "What that role sees",
      cell: (row) => String(row.posture ?? ""),
    },
  ];
  const deniedCollection = useCollection<TableRow>({
    rows: permissionDeniedRows,
    columns: deniedColumns,
    pageSize: 25,
    getRowKey: (row) => String(row.id ?? row.role ?? Math.random()),
    views: "coaching-permissions-denied-roles",
  });

  return (
    <>
      <Alert variant="info">
        Permission templates, view-as, history, billing, and credential management arrive once audited backend
        support exists.
      </Alert>
      {/* F3: real @vjal/ui ProTable-1 recipe export for permissions IR (feature catalog). */}
      <ProTable
        collection={featureCollection}
        className="p-0"
        tableProps={{
          caption: "Upcoming permission features",
          emptyTitle: "No permission features listed",
          emptyMessage: "Feature catalog will appear here when the permissions backend is ready.",
        }}
        stableChipNames
      />
      {/* F3: second ProTable body for denied-roles matrix (no residual DataTable). */}
      <ProTable
        collection={deniedCollection}
        className="p-0"
        searchable={false}
        showPagination={false}
        tableProps={{
          caption: "Access for other roles",
          emptyTitle: "No role access rows",
          emptyMessage: "Denied-role matrix is empty.",
        }}
        stableChipNames
      />
      {/* Keep column defs referenced so contract typing stays aligned. */}
      <span className="sr-only">{permissionColumns.length + permissionDeniedColumns.length}</span>
    </>
  );
}

function inferVjalComponents(contract: ProductScreenContract, backendBlocked: boolean) {
  const components = new Set<string>(["Module", "ModuleBody"]);

  if (contract.rows.length > 0 && contract.columns.length > 0) components.add("DataTable");
  if (contract.states.some((state) => ["error", "denied", "read-only", "blocked-backend"].includes(state))) components.add("Alert");
  if (contract.states.some((state) => ["empty", "denied"].includes(state))) components.add("EmptyState");
  if (contract.states.some((state) => ["ready", "editable", "download-authorized", "review-gated"].includes(state))) components.add("Badge");
  if (contract.surfaceMode === "focused") components.add("ModuleHead");
  if (backendBlocked) components.add("Badge");

  return [...components];
}

function inferLocalCompositions(routeId: string, contract: ProductScreenContract, compatibilityAlias: boolean) {
  const compositions = new Set<string>();

  if (contract.customBody) compositions.add(`${routeId} focused product surface`);
  if (contract.surfaceMode !== "focused") {
    compositions.add("OperationalSurface");
    compositions.add("CapabilityButton");
  }
  if (compatibilityAlias) compositions.add("compatibility alias contract");

  return [...compositions];
}

function inferDecisionHierarchy(contract: ProductScreenContract, backendBlocked: boolean, compatibilityAlias: boolean) {
  if (backendBlocked) {
    return {
      firstViewport: "Feature gate and no-data blocker copy.",
      primaryAction: "Prevent operational use until backend support is source-backed.",
      secondaryPanel: "Backend/catalog blocker reference.",
      deferredSurface: "Future implementation after API support lands.",
      gatedControls: "All data-bearing and mutation controls remain unavailable."
    };
  }

  if (compatibilityAlias) {
    return {
      firstViewport: "Standard product surface rendered through a compatibility route.",
      primaryAction: "Move users into the canonical route flow.",
      secondaryPanel: "Alias proof and route naming cleanup.",
      deferredSurface: "Redirect or retire alias after link cleanup.",
      gatedControls: "No alias-only controls or data behavior."
    };
  }

  return {
    firstViewport: `${contract.workTitle} plus the role-safe primary context.`,
    primaryAction: contract.primaryJob,
    secondaryPanel: contract.secondaryJob,
    deferredSurface: contract.extensionSlots.join(", "),
    gatedControls: contract.roleDeltas
  };
}

function inferRequiredViewports(contract: ProductScreenContract) {
  const viewports = new Set<string>(["desktop"]);
  if (/mobile|stack|no-overlap|no-overflow|single-column/i.test(contract.mobilePosture)) viewports.add("mobile");
  return [...viewports];
}

function inferMobileEvidencePaths(routeId: string, contract: ProductScreenContract) {
  if (!(contract.requiredViewports ?? inferRequiredViewports(contract)).includes("mobile")) return [];
  return mobileEvidencePaths[routeId] ?? screenEvidencePaths[routeId] ?? [];
}

function inferClosureState(
  routeId: string,
  contract: ProductScreenContract,
  backendBlocked: boolean,
  compatibilityAlias: boolean
): ProductScreenContract["closureState"] {
  if (backendBlocked) return "backend-blocked";
  if (compatibilityAlias) return "compatibility-alias";
  if (contract.surfaceMode === "focused") return "focused";
  if (foundationShellOwners[routeId]) return "foundation-shell";
  return "foundation-shell";
}

function inferClosureOwner(
  routeId: string,
  contract: ProductScreenContract,
  backendBlocked: boolean,
  compatibilityAlias: boolean
) {
  if (backendBlocked) return contract.extensionSlots[0] ?? "backend-blocked";
  if (compatibilityAlias) return foundationShellOwners[routeId] ?? "VIL-20260608-0024";
  if (contract.surfaceMode === "focused") return "VIL-20260608-0024";
  return foundationShellOwners[routeId] ?? "VIL-20260608-0024";
}
